import { observer } from "mobx-react-lite";
import styles from "./KeyValueInput.module.scss";
import React, { useCallback, useMemo } from "react";
import omitBy from "lodash/omitBy";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import classNames from "classnames";
import { randomString } from "@whatslly/commons/utils";
import { Select, AsyncSelect } from "@whatslly/ui-components/v2/BaseElements/Select";

type KeysInsideTheObject = string;
type ValueTypeOption = "string" | "boolean" | "numeric";

type LoadOptionsFunction = (inputValue: string) => Promise<{
  options: { label: string; value: string }[];
}>;

type KeyValueBaseProps = {
  typeOfValue: ValueTypeOption;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
} & (
  | {
      keyType?: "text";
      loadOptionsForKeyFunction?: never;
      keySelectValueFormatter?: never;
    }
  | {
      keyType: "asyncSelect";
      loadOptionsForKeyFunction: LoadOptionsFunction;
      keySelectValueFormatter: (userId: string) => { label: string; value: string } | undefined;
    }
);

type KeyValueInputProps = KeyValueBaseProps & {
  defaultValue?: Record<string, any>;
  onChange: (newValue: Record<string, string | boolean | number>) => void;
  rowContainerClassName?: string;
  errors?: KeysInsideTheObject[];
};

export const KeyValueInput = observer(
  ({
    defaultValue,
    onChange,
    typeOfValue,
    keyType = "text",
    rowContainerClassName,
    errors,
    keyPlaceholder,
    valuePlaceholder,
    loadOptionsForKeyFunction,
    keySelectValueFormatter,
  }: KeyValueInputProps) => {
    const [items, setItems] = React.useState(() => Object.entries(defaultValue ?? {}).map(([key, value]) => [key, value]));
    const handleOnChange = useCallback(
      (type: "key" | "value", newValue: string | number | boolean, indexInArray: number) => {
        const newValueAsArray = [...items];
        const newKey = type === "key" ? newValue : items[indexInArray][0];
        const newValueToSet = type === "value" ? newValue : items[indexInArray][1];
        newValueAsArray[indexInArray] = [newKey, newValueToSet];
        setItems(newValueAsArray);
        onChange?.(omitBy(Object.fromEntries(newValueAsArray), (value) => value === undefined));
      },
      [items, onChange]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const rowsUniqueKeys = useMemo(() => items.map(() => randomString(5)), [items.length]);

    const handleAddRow = useCallback(
      (isBoolean?: boolean) => {
        setItems([...items, ["", isBoolean ? true : ""]]);
      },
      [items]
    );

    const handleRemoveRow = useCallback(
      (rowNumber: number) => {
        setItems(items.filter((rowData, i) => i !== rowNumber));

        onChange?.(omitBy(Object.fromEntries(items.filter((rowData, i) => i !== rowNumber)) as any, (value) => value === undefined) || {});
      },
      [onChange, items]
    );

    return (
      <div className={styles.keyValueInputContainer}>
        {items.map(([key, value], i) => {
          const commonProps = {
            key: rowsUniqueKeys[i],
            keyInObject: key,
            value,
            onChange: (type: "key" | "value", newValue: string | number | boolean) => handleOnChange(type, newValue, i),
            typeOfValue,
            onKeyChange: (newValue: string) => handleOnChange("key", newValue, i),
            removeRow: () => handleRemoveRow(i),
            containerClassName: rowContainerClassName,
            hasError: errors?.includes(key),
            keyPlaceholder,
            valuePlaceholder,
          };
          if (keyType === "asyncSelect" && loadOptionsForKeyFunction && keySelectValueFormatter) {
            return (
              <KeyValueRow
                {...commonProps}
                keyType="asyncSelect"
                loadOptionsForKeyFunction={loadOptionsForKeyFunction}
                keySelectValueFormatter={keySelectValueFormatter}
              />
            );
          }

          return <KeyValueRow {...commonProps} keyType="text" />;
        })}
        <div data-testid="kv-add-row" className={styles.addRowButton} onClick={() => handleAddRow(typeOfValue === "boolean")}>
          +
        </div>
      </div>
    );
  }
);

type KeyValueRowProps = KeyValueBaseProps & {
  keyInObject: string;
  value: string | number | boolean;
  onChange: (type: "key" | "value", newValue: string | number | boolean) => void;
  onKeyChange: (newValue: string) => void;
  removeRow: () => void;
  containerClassName?: string;
  hasError?: boolean;
};

const KeyValueRow = React.memo(
  ({
    keyInObject,
    value,
    onChange,
    typeOfValue,
    keyType,
    removeRow,
    keyPlaceholder,
    valuePlaceholder,
    containerClassName,
    hasError,
    loadOptionsForKeyFunction,
    keySelectValueFormatter,
    onKeyChange,
  }: KeyValueRowProps) => {
    // Format the value for AsyncSelect for the key if needed
    const formattedKeyAsyncValue = useMemo(() => {
      if (keyType === "asyncSelect" && keyInObject && keySelectValueFormatter) {
        return keySelectValueFormatter(keyInObject);
      }
      return undefined;
    }, [keySelectValueFormatter, keyType, keyInObject]);

    return (
      <div data-testid="kv-row" className={classNames(styles.keyValueInputItem, containerClassName, { [styles.error]: hasError })}>
        <div className={styles.keyContainer}>
          {keyType === "asyncSelect" ? (
            <AsyncSelect
              data-testid="kv-key-select"
              selectSize="small"
              placeholder={keyPlaceholder}
              value={formattedKeyAsyncValue}
              loadOptionsFromServerFunction={loadOptionsForKeyFunction}
              onChange={(selectedOption: { label: string; value: string } | null) => onKeyChange(selectedOption?.value || "")}
            />
          ) : (
            <TextInput
              data-testid="kv-key-input"
              paddingInline={`none`}
              type="text"
              value={keyInObject}
              placeholder={keyPlaceholder}
              onChange={(e) => onChange(`key`, e.target.value)}
              onClear={() => onChange(`key`, "")}
            />
          )}
        </div>
        <span className={styles.objectRowSeparator}>:</span>
        <div className={styles.valueContainer}>
          {typeOfValue === "boolean" ? (
            <Select
              data-testid="kv-value-select"
              controlClassName={styles.keyValueBooleanSelect}
              selectSize="small"
              options={[
                { label: "True", value: true },
                { label: "False", value: false },
              ]}
              placeholder={valuePlaceholder}
              value={value === true || value === "true" ? { label: "True", value: true } : { label: "False", value: false }}
              onChange={({ value }: { value: boolean }) => onChange(`value`, value)}
            />
          ) : typeOfValue === "numeric" ? (
            <TextInput
              data-testid="kv-value-input"
              paddingInline={`none`}
              type="number"
              placeholder={valuePlaceholder}
              value={String(value)}
              onChange={(e) => onChange(`value`, e.target.value)}
              onClear={() => onChange(`value`, "")}
            />
          ) : (
            <TextInput
              data-testid="kv-value-input"
              type="text"
              paddingInline={`none`}
              value={String(value)}
              placeholder={valuePlaceholder}
              onChange={(e) => onChange(`value`, e.target.value)}
              onClear={() => onChange(`value`, "")}
            />
          )}
        </div>
        <div className={styles.removeRowButtonContainer}>
          <div data-testid="kv-remove-row" onClick={removeRow} className={styles.removeRowButton}>
            -
          </div>
        </div>
      </div>
    );
  }
);
